import { useMemo, useState } from "react";
import { Smartphone, Tablet, ExternalLink, Gauge, Lightbulb } from "lucide-react";
import { useCatalog } from "../lib/useCatalog";
import { evaluate, recommendedQuant } from "../lib/compute";
import { VerdictBadge } from "../components/VerdictBadge";
import { FavoriteButton, catalogFavorite } from "../components/FavoriteButton";
import { IOS_APPS } from "../components/InstallCommands";
import { LOCALLY_AI_URL } from "../components/assistant/FreeBanner";
import { MobileCompare } from "../components/MobileCompare";

const OTHER_IOS_APPS = IOS_APPS.filter((a) => !a.top);

export const DEVICES = [
  { id: "ip-4", type: "iphone", label: "iPhone 12 / 13 / 13 mini", ram: 4, speed: 30 },
  { id: "ip-6", type: "iphone", label: "iPhone 13 Pro / 14 / 15", ram: 6, speed: 40 },
  { id: "ip-8", type: "iphone", label: "iPhone 15 Pro / 16 / 16 Pro", ram: 8, speed: 60 },
  { id: "ip-12", type: "iphone", label: "iPhone 17 Pro / Pro Max", ram: 12, speed: 75 },
  { id: "pad-6", type: "ipad", label: "iPad (A16) / iPad mini 6", ram: 6, speed: 40 },
  { id: "pad-8", type: "ipad", label: "iPad Air M2-M3 / mini A17 Pro", ram: 8, speed: 80 },
  { id: "pad-16", type: "ipad", label: "iPad Pro M4 (1-2 To)", ram: 16, speed: 110 },
];

const TIPS = [
  "Fermez les autres apps avant de lancer un modèle : iOS réserve environ la moitié de la mémoire à une seule app.",
  "Commencez petit (1B à 3B) : la réponse est plus rapide et le téléphone chauffe moins.",
  "Une fois le modèle téléchargé, tout fonctionne en mode avion : rien ne quitte l'appareil.",
  "Branchez l'appareil pour les longues conversations, la génération consomme beaucoup de batterie.",
];

function DevicePicker({ device, setDevice }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="mobile-device-picker">
      {DEVICES.map((d) => {
        const Icon = d.type === "ipad" ? Tablet : Smartphone;
        return (
          <button key={d.id} onClick={() => setDevice(d)} data-testid={`mobile-device-${d.id}`}
            className={`flex items-center gap-2 rounded-lg border p-3 text-left transition-colors ${device.id === d.id ? "border-primary bg-primary/10" : "border-[#1E2638] bg-[#0F141C] hover:border-primary/60"}`}>
            <Icon className={`h-5 w-5 shrink-0 ${device.id === d.id ? "text-primary" : "text-muted-foreground"}`} />
            <span className="min-w-0">
              <span className="block truncate text-sm text-slate-200">{d.label}</span>
              <span className="font-mono2 text-[11px] text-muted-foreground">{d.ram} Go de mémoire</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MobileModel({ m, q, ev, tok }) {
  return (
    <div data-testid="mobile-model-card" className={`flex flex-col gap-2 rounded-xl border border-[#1E2638] bg-[#0F141C] p-4 ${ev.verdict === "no_go" ? "opacity-50" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono2 text-[11px] text-primary">{m.family} · {m.params}B</p>
          <h3 className="truncate font-head font-bold">{m.name}</h3>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <VerdictBadge verdict={ev.verdict} />
          <FavoriteButton item={catalogFavorite(m)} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{m.desc}</p>
      <p className="font-mono2 text-xs text-slate-300" data-testid="mobile-model-estimate">
        {ev.verdict === "no_go" ? "Trop lourd pour cet appareil" : <><Gauge className="inline h-3 w-3" /> ~{tok} tok/s · {q.label} · ~{ev.requiredGB.toFixed(1)} Go</>}
      </p>
      {ev.verdict !== "no_go" && <p className="text-[11px] text-muted-foreground">Dans Locally AI, cherchez « {m.family} » puis la taille {m.params < 2 ? "1B / 1.5B" : `${Math.round(m.params)}B`}.</p>}
    </div>
  );
}

export function mobileRows(data, device) {
  if (!data) return [];
  const hw = { accelerator: "metal", ram: device.ram * 0.55, vram: 0 };
  return data.models
    .filter((m) => m.use_cases.includes("mobile") || m.params <= 4)
    .map((m) => {
      const q = recommendedQuant(m, hw, data.quantizations);
      const ev = evaluate(m, q, hw);
      return { m, q, ev, tok: ev.verdict === "no_go" ? 0 : Math.max(2, Math.round(device.speed / m.params)) };
    })
    .sort((a, b) => (a.ev.verdict === "no_go") - (b.ev.verdict === "no_go") || a.m.params - b.m.params);
}

export default function Mobile() {
  const { data } = useCatalog();
  const [device, setDevice] = useState(DEVICES[2]);
  const rows = useMemo(() => mobileRows(data, device), [data, device]);
  const fits = rows.filter((r) => r.ev.verdict !== "no_go").length;

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-10 md:px-8" data-testid="mobile-page">
      <div>
        <span className="flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-widest text-primary"><Smartphone className="h-4 w-4" /> Sur iPhone et iPad</span>
        <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">Une IA privée dans votre poche</h1>
        <p className="mt-1 text-muted-foreground">Choisissez votre appareil : on vous montre les modèles qui tournent hors ligne, la vitesse estimée et comment les installer gratuitement.</p>
      </div>

      <section className="space-y-3">
        <h2 className="font-head text-lg font-bold">1. Votre appareil</h2>
        <DevicePicker device={device} setDevice={setDevice} />
      </section>

      <section className="space-y-3">
        <h2 className="font-head text-lg font-bold">2. Installez Locally AI (gratuit)</h2>
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <ol className="space-y-2 rounded-xl border border-primary/50 bg-[#0F141C] p-5 text-sm text-slate-300" data-testid="mobile-locally-steps">
            <li>1. Téléchargez <a href={LOCALLY_AI_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-locally-link" className="inline-flex items-center gap-1 text-primary hover:underline">Locally AI sur l'App Store <ExternalLink className="h-3 w-3" /></a> (par LM Studio, optimisé pour les puces Apple).</li>
            <li>2. Ouvrez l'app, puis « Manage Models ».</li>
            <li>3. Téléchargez un modèle de la liste ci-dessous (Wi-Fi conseillé, de 0,5 à 3 Go).</li>
            <li>4. Discutez : tout fonctionne hors ligne, même en mode avion.</li>
          </ol>
          <div className="space-y-2 rounded-xl border border-[#1E2638] bg-[#0F141C] p-4" data-testid="mobile-other-apps">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Autres apps</div>
            {OTHER_IOS_APPS.map((a) => (
              <a key={a.name} href={a.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 text-sm text-slate-300 hover:text-primary">
                {a.name} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-head text-lg font-bold">3. Modèles pour {device.label} <span className="font-mono2 text-sm font-normal text-muted-foreground" data-testid="mobile-fit-count">· {fits} compatible(s)</span></h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => <MobileModel key={r.m.id} {...r} />)}
        </div>
        <p className="text-xs text-muted-foreground">Vitesses estimées, à titre indicatif : elles varient selon l'app, la température et la longueur des réponses.</p>
      </section>

      <MobileCompare data={data} />

      <section className="rounded-xl border border-[#1E2638] bg-[#0F141C] p-5" data-testid="mobile-tips">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium"><Lightbulb className="h-4 w-4 text-amber-400" /> Conseils</div>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{TIPS.map((t) => <li key={t}>{t}</li>)}</ul>
      </section>
    </div>
  );
}
